import { GET, POST, PATCH, DELETE, request } from '@/shared/api/requests';
import { createWalkthroughResources } from '@ironview-360/api-client/walkthroughs';
import { packageTransport } from '@/shared/api/package-transport';
import { uploadResumableVideo } from './video-upload';
import type {
  PunchListItem,
  PunchListItemSummary,
  PunchListItemsResponse,
  PunchListStatusSummaryEntry,
  PunchListItemType,
  Trade,
  CostCode,
  CreatePunchListItemData,
  CreatePunchListFilterViewData,
  UpdatePunchListItemData,
  PunchListFilterView,
  PunchListItemFilters,
  SendPunchListRemindersData,
  SendPunchListRemindersResponse,
  ShareUnitPunchListReportsData,
  ShareUnitPunchListReportsResponse,
  PunchListReportImage,
  PunchListAttachmentKind,
  UserMinimal,
  TradePartnerMinimal,
  CreatePunchWorkflowBatchData,
  AddPunchWorkflowBatchRunData,
  CreatePunchWorkflowRunData,
  CreatePunchItemWorkflowData,
  PunchWorkflowBatch,
  PunchWorkflowRun,
  PunchItemWorkflow,
  PunchListExportPreferences,
  PunchListCsvColumnKey,
  PunchWalkthroughSession,
  PunchWalkthroughUploadResponse,
  QuickPunchConfigurationResponse,
  QuickPunchFieldKey,
  QuickPunchDefaultValue,
} from '../types';

const BASE = '/punchlists';

const punchWalkthroughContentType = (file: File) => {
  if (file.type) return file.type.split(';')[0];
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'mov') return 'video/quicktime';
  if (extension === 'webm') return 'video/webm';
  return 'video/mp4';
};

// ─── AI walkthrough capture ──────────────────────────────────────────────────

const walkthroughApi = createWalkthroughResources(packageTransport);

export const createPunchWalkthrough = (projectId: number, file: File) => walkthroughApi.create(projectId, {
  file_name: file.name, content_type: punchWalkthroughContentType(file), file_size: file.size,
  upload_mode: 'resumable',
});

export const finalizePunchWalkthrough = walkthroughApi.finalize;
export const getPunchWalkthrough = walkthroughApi.get;
export const getPunchWalkthroughs = walkthroughApi.list;
export const publishPunchWalkthrough = walkthroughApi.publish;
export const reviewPunchWalkthroughItem = walkthroughApi.reviewItem;
export const savePunchWalkthroughDrafts = walkthroughApi.save;
export const uploadPunchWalkthroughPhoto = (projectId: number, sessionId: string,
  draftId: string, revision: number, file: File) => {
  const body = new FormData();
  body.append('project_id', String(projectId));
  body.append('draft_id', draftId);
  body.append('revision', String(revision));
  body.append('file', file);
  return walkthroughApi.uploadPhoto(projectId, sessionId, body);
};
export const retryPunchWalkthrough = walkthroughApi.retry;
export const cancelPunchWalkthrough = walkthroughApi.cancel;

export const uploadPunchWalkthroughVideo = uploadResumableVideo;

export const uploadAndFinalizePunchWalkthrough = async (
  projectId: number,
  file: File,
  onProgress?: (progressPct: number) => void,
  onCreated?: (upload: PunchWalkthroughUploadResponse) => void,
  resume?: PunchWalkthroughUploadResponse,
): Promise<PunchWalkthroughSession> => {
  const created = resume ?? await createPunchWalkthrough(projectId, file);
  onCreated?.(created);
  await uploadResumableVideo(file, created.signed_upload_url, onProgress);
  return finalizePunchWalkthrough(projectId, created.session.id);
};

// ─── Items ───────────────────────────────────────────────────────────────────

export const getPunchListItemsPage = async (
  projectId: number,
  filters: PunchListItemFilters = {},
  next: string | null = null,
  signal?: AbortSignal,
): Promise<PunchListItemsResponse> => {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.assignee) params.set('assignee', String(filters.assignee));
  if (filters.search) params.set('search', filters.search);
  if (filters.hotspot_id) params.set('hotspot_id', String(filters.hotspot_id));
  if (filters.photo_id) params.set('photo_id', String(filters.photo_id));
  if (filters.area_tag_id) params.set('area_tag_id', String(filters.area_tag_id));
  if (filters.area_tag_set_id) params.set('area_tag_set_id', String(filters.area_tag_set_id));
  if (filters.layout_id) params.set('layout_id', String(filters.layout_id));
  const endpoint = next ? toRelativeApiEndpoint(next) : `${BASE}/items/?${params}`;
  const response = signal
    ? await request('get', endpoint, undefined, undefined, signal)
    : await GET(endpoint);
  const data = response.data;
  return Array.isArray(data)
    ? { results: data, count: data.length, next: null, previous: null }
    : data as PunchListItemsResponse;
};

// Reports and explicit exports still await the complete result set.
export const getPunchListItems = async (
  projectId: number,
  filters: PunchListItemFilters = {},
): Promise<PunchListItemSummary[]> => {
  const results: PunchListItemSummary[] = [];
  let next: string | null = null;
  do {
    const page = await getPunchListItemsPage(projectId, filters, next);
    results.push(...page.results);
    next = page.next;
  } while (next);
  return results;
};

const toRelativeApiEndpoint = (url: string) => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname.replace(/^\/api\/v1/, '')}${parsed.search}`;
  } catch {
    return url;
  }
};

export const getPunchListItemById = async (
  id: string,
  projectId: number
): Promise<PunchListItem> => {
  const response = await GET(`${BASE}/items/${id}/?project_id=${projectId}`);
  return response.data as PunchListItem;
};

export const createPunchListItem = async (
  data: CreatePunchListItemData
): Promise<PunchListItem> => {
  const response = await POST(`${BASE}/items/`, data);
  return response.data as PunchListItem;
};

export const updatePunchListItem = async (
  id: string,
  data: UpdatePunchListItemData
): Promise<PunchListItem> => {
  const response = await PATCH(`${BASE}/items/${id}/`, data);
  return response.data as PunchListItem;
};

export const deletePunchListItem = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/items/${id}/`);
};

export const approvePunchListItem = async (id: string): Promise<PunchListItem> => {
  const response = await POST(`${BASE}/items/${id}/approve/`, {});
  return response.data as PunchListItem;
};

export const rejectPunchListItem = async (id: string): Promise<PunchListItem> => {
  const response = await POST(`${BASE}/items/${id}/reject/`, {});
  return response.data as PunchListItem;
};

export const sendPunchListReminders = async (
  data: SendPunchListRemindersData
): Promise<SendPunchListRemindersResponse> => {
  const response = await POST(`${BASE}/items/send-reminders/`, data);
  return response.data as SendPunchListRemindersResponse;
};

export const shareUnitPunchListReports = async (
  data: ShareUnitPunchListReportsData
): Promise<ShareUnitPunchListReportsResponse> => {
  const response = await POST(`${BASE}/unit-reports/share/`, data);
  return response.data as ShareUnitPunchListReportsResponse;
};

export const getPunchListStatusSummary = async (
  projectId: number
): Promise<PunchListStatusSummaryEntry[]> => {
  const response = await GET(`${BASE}/items/status-summary/?project_id=${projectId}`);
  const data = response.data;
  if (Array.isArray(data)) return data as PunchListStatusSummaryEntry[];
  return (data as { results: PunchListStatusSummaryEntry[] }).results ?? [];
};

export const getPunchListExportPreferences = async (
  projectId: number
): Promise<PunchListExportPreferences> => {
  const response = await GET(`${BASE}/export-preferences/?project_id=${projectId}`);
  return response.data as PunchListExportPreferences;
};

export const updatePunchListExportPreferences = async (
  projectId: number,
  selectedColumns: PunchListCsvColumnKey[]
): Promise<PunchListExportPreferences> => {
  const response = await PATCH(`${BASE}/export-preferences/?project_id=${projectId}`, {
    selected_columns: selectedColumns,
  });
  return response.data as PunchListExportPreferences;
};

export const getQuickPunchConfiguration = async (
  projectId: number
): Promise<QuickPunchConfigurationResponse> => {
  const response = await GET(`${BASE}/quick-punch/configuration/?project_id=${projectId}`);
  return response.data as QuickPunchConfigurationResponse;
};

export const updateQuickPunchPreference = async (
  projectId: number,
  payload: {
    visible_fields: QuickPunchFieldKey[];
    required_fields: QuickPunchFieldKey[];
    defaults: Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;
  }
): Promise<QuickPunchConfigurationResponse> => {
  const response = await PATCH(
    `${BASE}/quick-punch/configuration/?project_id=${projectId}`,
    payload
  );
  return response.data as QuickPunchConfigurationResponse;
};

export const getQuickPunchProjectConfiguration = async (
  projectId: number
): Promise<QuickPunchConfigurationResponse> => {
  const response = await GET(`${BASE}/quick-punch/project-configuration/?project_id=${projectId}`);
  return response.data as QuickPunchConfigurationResponse;
};

export const updateQuickPunchProjectConfiguration = async (
  projectId: number,
  payload: {
    required_fields: QuickPunchFieldKey[];
    defaults: Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;
  }
): Promise<QuickPunchConfigurationResponse> => {
  const response = await PATCH(
    `${BASE}/quick-punch/project-configuration/?project_id=${projectId}`,
    payload
  );
  return response.data as QuickPunchConfigurationResponse;
};

export const createQuickPunchItem = async (
  data: Partial<CreatePunchListItemData> & Pick<CreatePunchListItemData, 'project' | 'description'>
): Promise<PunchListItem> => {
  const response = await POST(`${BASE}/quick-punch/items/?project_id=${data.project}`, data);
  return response.data as PunchListItem;
};

// ─── Saved filter views ──────────────────────────────────────────────────────

export const getPunchListFilterViews = async (
  projectId: number
): Promise<PunchListFilterView[]> => {
  const response = await GET(`${BASE}/filter-views/?project_id=${projectId}`);
  const data = response.data;
  if (Array.isArray(data)) return data as PunchListFilterView[];
  return (data as { results: PunchListFilterView[] }).results ?? [];
};

export const createPunchListFilterView = async (
  data: CreatePunchListFilterViewData
): Promise<PunchListFilterView> => {
  const response = await POST(`${BASE}/filter-views/`, data);
  return response.data as PunchListFilterView;
};

export const deletePunchListFilterView = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/filter-views/${id}/`);
};

// ─── Dropdown options ─────────────────────────────────────────────────────────

export const getPunchListItemTypes = async (): Promise<PunchListItemType[]> => {
  const response = await GET(`${BASE}/item-types/`);
  const data = response.data;
  if (Array.isArray(data)) return data as PunchListItemType[];
  return (data as { results: PunchListItemType[] }).results ?? [];
};

export const createPunchListItemType = async (name: string): Promise<PunchListItemType> => {
  const response = await POST(`${BASE}/item-types/`, { name });
  return response.data as PunchListItemType;
};

export const getTrades = async (): Promise<Trade[]> => {
  const response = await GET(`${BASE}/trades/`);
  const data = response.data;
  if (Array.isArray(data)) return data as Trade[];
  return (data as { results: Trade[] }).results ?? [];
};

export const createTrade = async (name: string): Promise<Trade> => {
  const response = await POST(`${BASE}/trades/`, { name });
  return response.data as Trade;
};

export const getTradePartners = async (projectId: number): Promise<TradePartnerMinimal[]> => {
  const response = await GET(`${BASE}/trade-partners/?project_id=${projectId}`);
  const data = response.data;
  if (Array.isArray(data)) return data as TradePartnerMinimal[];
  return (data as { results: TradePartnerMinimal[] }).results ?? [];
};

export const getCostCodes = async (): Promise<CostCode[]> => {
  const response = await GET(`${BASE}/cost-codes/`);
  const data = response.data;
  if (Array.isArray(data)) return data as CostCode[];
  return (data as { results: CostCode[] }).results ?? [];
};

export const createCostCode = async (payload: {
  name: string;
  code: string;
}): Promise<CostCode> => {
  const response = await POST(`${BASE}/cost-codes/`, payload);
  return response.data as CostCode;
};

// ─── Project users (for person pickers) ──────────────────────────────────────

export const getProjectShareableUsers = async (projectId: number): Promise<UserMinimal[]> => {
  const response = await GET(`/base/projects/${projectId}/shareable-users/`);
  const data = response.data;
  if (Array.isArray(data)) return data as UserMinimal[];
  // The endpoint returns { users: [...] } or a paginated shape
  if (data.users) return data.users as UserMinimal[];
  return (data as { results: UserMinimal[] }).results ?? [];
};

// ─── Area tags (location picker) ─────────────────────────────────────────────

export const createAreaTagForProject = async (payload: {
  name: string;
  project_id: number;
  layout_id: number;
}): Promise<{ id: number; name: string }> => {
  const response = await POST(`${BASE}/locations/create/`, payload);
  return response.data as { id: number; name: string };
};

// ─── Attachments ─────────────────────────────────────────────────────────────

export const uploadPunchListAttachment = async (
  itemId: string,
  file: File,
  kind: PunchListAttachmentKind = 'general'
): Promise<void> => {
  const formData = new FormData();
  formData.append('item', itemId);
  formData.append('file', file);
  formData.append('kind', kind);
  await POST(`${BASE}/attachments/`, formData);
};

export const linkPunchListAttachmentPhoto = async (
  itemId: string,
  photoId: number,
  kind: PunchListAttachmentKind = 'general'
): Promise<void> => {
  await POST(`${BASE}/attachments/`, { item: itemId, photo: photoId, kind });
};

export const deletePunchListAttachment = async (id: string): Promise<void> => {
  await DELETE(`${BASE}/attachments/${id}/`);
};

export const getPunchListReportImages = async (
  projectId: number,
  itemIds: string[]
): Promise<PunchListReportImage[]> => {
  const response = await POST(`${BASE}/items/report-images/`, {
    project_id: projectId,
    item_ids: itemIds,
  });
  return (response.data as { images: PunchListReportImage[] }).images;
};

// ─── Punch workflows ─────────────────────────────────────────────────────────

const normalizeList = <T>(data: T[] | { results?: T[] }): T[] =>
  Array.isArray(data) ? data : (data.results ?? []);

export const getPunchItemWorkflowTemplates = async (
  projectId: number
): Promise<PunchItemWorkflow[]> => {
  const response = await GET(`${BASE}/workflows/?project_id=${projectId}`);
  return normalizeList(response.data as PunchItemWorkflow[] | { results?: PunchItemWorkflow[] });
};

export const createPunchItemWorkflow = async (
  data: CreatePunchItemWorkflowData
): Promise<PunchItemWorkflow> => {
  const response = await POST(`${BASE}/workflows/?project_id=${data.project}`, data);
  return response.data as PunchItemWorkflow;
};

export const updatePunchItemWorkflow = async (
  id: string,
  projectId: number,
  data: CreatePunchItemWorkflowData
): Promise<PunchItemWorkflow> => {
  const response = await PATCH(`${BASE}/workflows/${id}/?project_id=${projectId}`, data);
  return response.data as PunchItemWorkflow;
};

export const archivePunchItemWorkflow = async (id: string, projectId: number): Promise<void> => {
  await DELETE(`${BASE}/workflows/${id}/?project_id=${projectId}`);
};

export const getPunchWorkflowRuns = async (projectId: number): Promise<PunchWorkflowRun[]> => {
  const response = await GET(`${BASE}/workflow-runs/?project_id=${projectId}`);
  return normalizeList(response.data as PunchWorkflowRun[] | { results?: PunchWorkflowRun[] });
};

export const getPunchWorkflowBatches = async (projectId: number): Promise<PunchWorkflowBatch[]> => {
  const response = await GET(`${BASE}/workflow-batches/?project_id=${projectId}`);
  return normalizeList(response.data as PunchWorkflowBatch[] | { results?: PunchWorkflowBatch[] });
};

export const getPunchWorkflowRun = async (
  projectId: number,
  runId: string
): Promise<PunchWorkflowRun> => {
  const response = await GET(`${BASE}/workflow-runs/${runId}/?project_id=${projectId}`);
  return response.data as PunchWorkflowRun;
};

export const createPunchWorkflowRun = async (
  projectId: number,
  data: CreatePunchWorkflowRunData
): Promise<PunchWorkflowRun> => {
  const response = await POST(`${BASE}/workflow-runs/?project_id=${projectId}`, data);
  return response.data as PunchWorkflowRun;
};

export const createPunchWorkflowBatch = async (
  projectId: number,
  data: CreatePunchWorkflowBatchData
): Promise<PunchWorkflowBatch> => {
  const response = await POST(`${BASE}/workflow-batches/?project_id=${projectId}`, data);
  return response.data as PunchWorkflowBatch;
};

export const addPunchWorkflowBatchRun = async (
  projectId: number,
  batchId: string,
  data: AddPunchWorkflowBatchRunData
): Promise<PunchWorkflowRun> => {
  const response = await POST(
    `${BASE}/workflow-batches/${batchId}/add-run/?project_id=${projectId}`,
    data
  );
  return response.data as PunchWorkflowRun;
};
